import styled from 'styled-components';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import { IconButton } from '@mui/material';
import Button from '@mui/material/Button';

type DrawerProps = {
  open: boolean;
  variant?: 'permanent' | 'persistent' | 'temporary';
};

type MainProps = {
  open: boolean;
};

export const Root = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  position: absolute;
`;

export const Logo = styled.img.attrs(() => ({
  src: '/LoginLogo.png',
}))`
  height: 27px;
  padding-left: 5px;
  cursor: pointer;
`;

export const TopAppbar = styled(AppBar).attrs(() => ({
  position: 'fixed',
  color: 'default',
}))`
  && {
    z-index: ${({ theme }) => theme.zIndex.drawer + 1};
    background: white;
    color: ${({ theme }) => theme.palette.primary.dark};
  }
`;

export const TopToolbar = styled(Toolbar)`
  && {
    padding: 0;
    margin-left: 10px;
    margin-right: 10px;
  }
`;

export const AppBarPlaceholder = styled.div`
  ${({ theme }: any) => theme.mixins.toolbar}
`;

export const Title = styled(Typography).attrs(() => ({
  variant: 'h6',
  noWrap: true,
}))`
  flex-grow: 1;
  color: ${({ theme }) => theme.palette.primary.dark};
  display: none;
`;

export const Main = styled.main<MainProps>`
  flex-grow: 1;
  height: fit-content !important;
  flex-direction: column;
  display: flex;
  ${({ open, theme }) =>
    open
      ? `
    margin-left: 0;
    transition: margin ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp};`
      : `margin-left: ${-theme.drawerWidth}px;
    transition: margin ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.sharp};`};
`;

export const SideDrawer = styled(Drawer)<DrawerProps>`
  flex-shrink: 0;
  width: ${({ theme }) => theme.drawerWidth}px;

  & > .MuiPaper-root {
    width: ${({ theme }) => theme.drawerWidth}px;
  }
  ${({ open, theme }) =>
    open
      ? `
      transform: none;
      transition: transform ${theme.transitions.duration.standard * 2}ms ${theme.transitions.easing.easeOut};`
      : `transform: translateX(-${theme.drawerWidth}px);
      transition: transform ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp};`};
`;

export const ScrollingContainer = styled.div`
  background: #27589e;
  height: 100%;
  padding: 14px 0;
  overflow-y: auto;
`;

export const MenuButton = styled(IconButton).attrs({
  edge: 'end',
  disableFocusRipple: true,
  disableRipple: true,
})`
  margin-left: -13px;
  margin-right: 0;
  && {
    color: rgba(0, 0, 0, 0.54);
    padding: 12px;
    background-color: transparent;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    border-radius: 50%;
    &.MuiIconButton-edgeEnd {
      margin-right: 0;
    }
    .MuiSvgIcon-root {
      font-size: 1.5rem;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }
`;

export const LogoutButton = styled(MenuButton).attrs({ disableRipple: true })`
  margin-right: -13px;
  && {
    color: #5f6368;
    padding: 12px;
    .MuiSvgIcon-root {
      font-size: 1.5rem;
      color: rgba(0, 0, 0, 0.54);
    }
    &:hover {
      background-color: rgba(95, 99, 104, 0.08);
    }
  }
`;

export const ActionsContainer = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0;
`;

export const ThematicLabButton = styled(Button)`
  && {
    text-transform: none;
    min-width: 0;
    border-radius: 12px;
    padding: 0 20px;
    height: 36px;
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: -0.05em;
    border-width: 1.5px;
    border-style: solid;
    border-color: #1976d2;
    color: #1565c0;
    background-color: transparent;
    box-shadow: none;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
    .MuiButton-startIcon {
      margin-left: 0;
      margin-right: 8px;
    }
    .MuiSvgIcon-root {
      font-size: 1.1rem;
    }
    &:hover {
      border-color: #1565c0;
      color: #0d5abf;
      background-color: rgba(21, 101, 192, 0.06);
    }
    &:focus-visible {
      outline: 3px solid rgba(21, 101, 192, 0.25);
      outline-offset: 2px;
    }
    &:active {
      background-color: rgba(21, 101, 192, 0.16);
      border-color: #0d5abf;
    }
    &:disabled {
      border-color: ${({ theme }) => theme.palette.action.disabledBackground};
      color: ${({ theme }) => theme.palette.text.disabled};
    }

    @media (max-width: 960px) {
      padding: 0 14px;
      font-size: 0.82rem;
    }
  }
`;
